import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService, LoanChargeConfig, LoanConfig } from '../../core/api.service';

@Component({
  selector: 'ch-loan-config',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './loan-config.component.html'
})
export class LoanConfigComponent implements OnInit {
  config: LoanConfig = {
    id: '',
    max_salary_percent_basis_points: 6667,
    interest_rate_basis_points: 1400,
    status: 'active',
    charges: []
  };
  loading = false;
  saving = false;
  error: string | null = null;
  notice: string | null = null;

  constructor(private readonly api: ApiService) {}

  ngOnInit(): void {
    this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      this.config = this.withChargeAmountsInShillings(await this.api.loanConfig());
    } catch {
      this.error = 'Could not load loan config.';
    } finally {
      this.loading = false;
    }
  }

  addCharge(): void {
    this.config.charges = [
      ...this.config.charges,
      { description: '', amount_cents: 0, status: 'active' }
    ];
  }

  removeCharge(index: number): void {
    const charge = this.config.charges[index];
    if (charge?.id) {
      this.config.charges[index] = { ...charge, status: 'inactive' };
      return;
    }
    this.config.charges = this.config.charges.filter((_, itemIndex) => itemIndex !== index);
  }

  async save(): Promise<void> {
    if (this.saving) return;
    this.saving = true;
    this.error = null;
    this.notice = null;
    try {
      this.config = await this.api.saveLoanConfig({
        ...this.config,
        charges: this.config.charges.map((charge: LoanChargeConfig) => ({
          ...charge,
          description: charge.description.trim(),
          amount_cents: Math.round(Number(charge.amount_cents || 0) * 100),
          status: charge.status || 'active'
        }))
      });
      this.config = this.withChargeAmountsInShillings(this.config);
      this.notice = 'Global loan config saved successfully.';
    } catch (error) {
      this.error = this.feedbackMessage(error, 'Could not save loan config.');
    } finally {
      this.saving = false;
    }
  }

  percent(value: number): string {
    return `${(Number(value || 0) / 100).toFixed(2)}%`;
  }

  money(cents: number): string {
    return `KSh ${Math.round(Number(cents || 0) / 100).toLocaleString()}`;
  }

  charges(): LoanChargeConfig[] {
    return this.config.charges || [];
  }

  private feedbackMessage(error: unknown, fallback: string): string {
    if (error instanceof Error && error.message.trim()) {
      return error.message.trim();
    }
    if (typeof error === 'string' && error.trim()) {
      return error.trim();
    }
    return fallback;
  }

  private withChargeAmountsInShillings(config: LoanConfig): LoanConfig {
    return {
      ...config,
      charges: (config.charges || []).map((charge) => ({
        ...charge,
        amount_cents: Math.round(Number(charge.amount_cents || 0) / 100)
      }))
    };
  }
}
